import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Job, jobMicroUsd } from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { LanguageCode, RelativeTime } from '../../task/TaskBadges';
import { formatMicroUsd } from '../formatCost';
import { jobIcon, truncate } from '../jobDisplay';
import { JobStatusPill } from '../JobBadges';
import { useListUserJobsInfiniteQuery } from '../jobEndpoints';

/** The characters a user job's document is cut to in the list (QZ-D47). */
const DOCUMENT_MAX = 80;

/**
 * The drafts a job left for review over every task it stored, auto-approved ones included
 * (QZ-D56, QZ-D161): "12/40". Copied from `JobsPage.tsx`, which this list shares no row
 * rendering with (its Document/Owner columns differ from Request).
 */
const draftsOfTasks = (job: Job): string => {
  const drafts = job.chunks.reduce((total, chunk) => total + chunk.drafted, 0);
  const approved = job.chunks.reduce((total, chunk) => total + chunk.approved, 0);
  return `${drafts}/${drafts + approved}`;
};

/** Whether a job is still being planned or generated, and so worth polling for. */
const isActive = (job: Job): boolean => job.status === 'planning' || job.status === 'running';

/**
 * The admin user-jobs list at `/user-jobs` (QZ-D197, EXJ-D3): the jobs GPS Quiz users have
 * started, newest first, with their document and owner in place of `JobsPage.tsx`'s Request
 * column. No spend cards and no New Job button: an admin never starts a user job here. Each
 * row opens the same job page as any job, at `/jobs/:jobId`.
 */
export const UserJobsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [pollingInterval, setPollingInterval] = useState(0);
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, error } =
    useListUserJobsInfiniteQuery(undefined, { pollingInterval, skipPollingIfUnfocused: true });

  const jobs = useMemo(() => data?.pages.flatMap((page) => page.jobs) ?? [], [data]);

  useEffect(() => {
    setPollingInterval(jobs.some(isActive) ? 10_000 : 0);
  }, [jobs]);

  const code = problemCode(error);
  const open = (jobId: string) => navigate(`/jobs/${jobId}`);

  return (
    <main className="qz-main">
      <div className="qz-container">
        <div className="qz-page-header">
          <div>
            <h1>{t('userJob.list.title')}</h1>
            <p className="qz-sub">{t('userJob.list.sub')}</p>
          </div>
        </div>

        <div className="qz-card">
          {isLoading && <p className="qz-card-body qz-muted mb-0">{t('job.list.loading')}</p>}
          {error && (
            <div className="qz-card-body">
              <div className="alert alert-danger mb-0">
                <Icon name="circle-exclamation" />
                <div>{t(code ? `error.${code}` : 'error.unknown')}</div>
              </div>
            </div>
          )}
          {!isLoading && !error && jobs.length === 0 && !hasNextPage && (
            <div className="qz-empty">
              <Icon name="wand-magic-sparkles" />
              <strong>{t('userJob.list.empty')}</strong>
            </div>
          )}
          {!isLoading && jobs.length > 0 && (
            <table className="table table-hover table-clickable align-middle qz-table-stack">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>{t('userJob.list.col.document')}</th>
                  <th>{t('userJob.list.col.owner')}</th>
                  <th>{t('job.field.language')}</th>
                  <th>{t('task.filter.status')}</th>
                  <th className="text-end">{t('job.field.draftsOfTasks')}</th>
                  <th className="text-end">{t('job.field.cost')}</th>
                  <th className="text-end">{t('job.field.created')}</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.jobId}
                    role="button"
                    tabIndex={0}
                    onClick={() => open(job.jobId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        open(job.jobId);
                      } else if (e.key === ' ') {
                        e.preventDefault();
                        open(job.jobId);
                      }
                    }}
                  >
                    <td>
                      <div className="fw-semibold">
                        <span className="qz-muted">
                          <Icon name={jobIcon(job)} />
                        </span>{' '}
                        {truncate(job.studyMaterial?.name ?? '', DOCUMENT_MAX)}
                      </div>
                    </td>
                    <td>{job.owner?.username}</td>
                    <td>
                      <LanguageCode language={job.language} />
                    </td>
                    <td>
                      <JobStatusPill job={job} showProgress />
                    </td>
                    <td className="text-end" data-label={t('job.field.draftsOfTasks')}>
                      {draftsOfTasks(job)}
                    </td>
                    <td className="text-end qz-mono" data-label={t('job.field.cost')}>
                      {formatMicroUsd(jobMicroUsd(job))}
                    </td>
                    <td className="text-end qz-small qz-muted">
                      <RelativeTime iso={job.createdAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!isLoading && jobs.length > 0 && hasNextPage && (
            <div className="qz-card-footer">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={isFetching}
                onClick={() => fetchNextPage()}
              >
                {t('job.list.loadMore')}
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default UserJobsPage;
